/**
 * The saved block as this app stores and passes it around. Deliberately not
 * either editor package's type: both runtimes ship while the `editor_v3` opt-out exists,
 * and their saved block types are not assignable to one another. This shape is
 * assignable to both.
 */
export interface ISavedBlockData {
    id: number;
    name: string;
    definition: unknown;
    img?: string;
}
import useConfirm from '@/composables/useConfirm';
import { route } from 'ziggy-js';
import axios from 'axios';
import { ref } from 'vue';
import IApiResponse from '@customTypes/IApiResponse';
import useEditorTranslations from './useEditorTranslations';
import { EDITOR_REQUEST_HEADERS } from '@/composables/useEditorSession';

/**
 * Shapes the stored saved blocks into what the editor takes, dropping any whose
 * definition cannot be read.
 *
 * The editor validates the whole array in one go and shows nothing at all when
 * a single entry fails its schema, so one row with a null or truncated
 * definition would otherwise hide every saved block the team has. Losing the
 * broken one is the lesser failure - and it is the only one the user can act on.
 */
export const prepareSavedBlocks = (
    savedBlocks: ISavedBlockData[]
): ISavedBlockData[] => {
    return savedBlocks.flatMap((savedBlock) => {
        let definition: unknown = savedBlock.definition;

        if (typeof definition === 'string') {
            try {
                definition = JSON.parse(definition);
            } catch (e) {
                definition = null;
            }
        }

        if (definition === null || definition === undefined) {
            console.warn(
                'Skipping saved block with an unreadable definition',
                savedBlock.id,
                savedBlock.name
            );

            return [];
        }

        return [
            {
                id: savedBlock.id,
                name: savedBlock.name,
                definition: [definition],
            },
        ];
    });
};

export default (
    initialSavedBlocks: ISavedBlockData[],
    saveSavedBlockUrl: string,
    language: string
) => {
    const { translations: editorTranslations, getTranslations } =
        useEditorTranslations(language);

    // @ts-ignore
    const translations = ref(editorTranslations[language]);

    //store saved block locally for future mutations
    const savedBlocks = ref<ISavedBlockData[]>(initialSavedBlocks);

    const { show } = useConfirm();

    const getSavedBlockNames = (): string[] => {
        const savedBlockNames: string[] = [];

        savedBlocks.value.map((savedBlock) => {
            savedBlockNames.push(savedBlock.name);
        });

        return savedBlockNames;
    };

    const createSavedBlock = async (newSavedBlockJson: ISavedBlockData) => {
        const savedBlockNames = getSavedBlockNames();

        const name = await show({
            type: 'info',
            hasInput: true,
            header: translations.value.savedBlockCreateTitle,
            inputData: {
                label: translations.value.savedBlockCreateLabel,
                placeholder: translations.value.savedBlockCreatePlaceholder,
                validator: (text) => {
                    return !savedBlockNames.includes(text);
                },
                validationErrorMessage: translations.value.savedBlockExistsText,
            },
            confirm: translations.value.savedBlockCreateTitle,
            cancel: translations.value.cancel,
        });

        if (name && typeof name === 'string') {
            try {
                await axios.post<IApiResponse<unknown>>(
                    saveSavedBlockUrl,
                    {
                        name: name,
                        definition: newSavedBlockJson.definition,
                    },
                    { headers: EDITOR_REQUEST_HEADERS }
                );

                return await reloadSavedBlocks();
            } catch (err) {
                throw new Error(translations.value.savedBlockCreateFailed);
            }
        }
    };

    const editSavedBlock = async (templateSavedBlockId: number) => {
        const savedBlock = savedBlocks.value.find(
            (savedBlock) => savedBlock.id === templateSavedBlockId
        );

        if (savedBlock === undefined) {
            return;
        }

        const savedBlockNames = getSavedBlockNames();

        const newName = await show({
            type: 'info',
            hasInput: true,
            header: translations.value.rename,
            inputData: {
                label: translations.value.savedBlockCreateLabel,
                placeholder: translations.value.savedBlockCreatePlaceholder,
                validator: (text) => {
                    return !savedBlockNames.includes(text);
                },
                validationErrorMessage: translations.value.savedBlockExistsText,
                defaultValue: savedBlock.name,
            },
            confirm: translations.value.rename,
            cancel: translations.value.cancel,
        });

        if (newName) {
            try {
                await axios.put<IApiResponse<unknown>>(
                    route('template-saved-blocks.update', {
                        templateSavedBlock: savedBlock.id,
                    }),
                    {
                        name: newName,
                    },
                    { headers: EDITOR_REQUEST_HEADERS }
                );

                return await reloadSavedBlocks();
            } catch (err) {
                throw new Error(translations.value.savedBlockEditFailed);
            }
        }
    };

    const deleteSavedBlock = async (savedBlockId: number) => {
        const savedBlockToBeDeleted = savedBlocks.value.find(
            (bl: ISavedBlockData) => bl.id === savedBlockId
        );

        if (savedBlockToBeDeleted !== undefined) {
            const confirmed = await show({
                message: getTranslations(
                    'savedBlockDeleteText',
                    savedBlockToBeDeleted.name
                ),
                type: 'danger',
                header: translations.value.delete,
                confirm: translations.value.delete,
                cancel: translations.value.cancel,
            });

            if (confirmed) {
                try {
                    await axios.delete(
                        route('template-saved-blocks.delete', {
                            templateSavedBlock: savedBlockToBeDeleted.id,
                        }),
                        { headers: EDITOR_REQUEST_HEADERS }
                    );

                    return await reloadSavedBlocks();
                } catch (err) {
                    return;
                }
            }
        }
    };

    const reloadSavedBlocks = async () => {
        try {
            const res = await axios.get<IApiResponse<ISavedBlockData[]>>(
                route('template-saved-blocks.index'),
                { headers: EDITOR_REQUEST_HEADERS }
            );

            const blocks = prepareSavedBlocks(res.data.data);
            savedBlocks.value = blocks;
            return blocks;
        } catch (err) {
            throw new Error(translations.value.savedBlockRefetchFailed);
        }
    };

    return {
        createSavedBlock,
        deleteSavedBlock,
        editSavedBlock,
        prepareSavedBlocks,
        savedBlocks,
    };
};
